// src/components/AIAgent/ProductCatalog.jsx

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSave,
  FaBox,
  FaDollarSign,
  FaTag,
} from "react-icons/fa";
import { useAIAgentStore } from "../../services/useAIAgentStore";

const ProductCatalog = () => {
  const {
    products,
    loading,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
  } = useAIAgentStore();

  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    short_description: "",
    price: "",
    price_type: "fixo",
    stock_available: true,
    delivery_time: "",
    category: "",
    keywords: [],
    faq: [],
  });
  const [newKeyword, setNewKeyword] = useState("");
  const [newFaq, setNewFaq] = useState({ question: "", answer: "" });

  useEffect(() => {
    fetchProducts();
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      short_description: "",
      price: "",
      price_type: "fixo",
      stock_available: true,
      delivery_time: "",
      category: "",
      keywords: [],
      faq: [],
    });
    setNewKeyword("");
    setNewFaq({ question: "", answer: "" });
    setEditingProduct(null);
  };

  const handleOpenForm = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || "",
        description: product.description || "",
        short_description: product.short_description || "",
        price: product.price || "",
        price_type: product.price_type || "fixo",
        stock_available: product.stock_available ?? true,
        delivery_time: product.delivery_time || "",
        category: product.category || "",
        keywords: product.keywords || [],
        faq: product.faq || [],
      });
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim())) {
      setFormData({
        ...formData,
        keywords: [...formData.keywords, newKeyword.trim()],
      });
      setNewKeyword("");
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter((k) => k !== keyword),
    });
  };

  const handleAddFaq = () => {
    if (newFaq.question.trim() && newFaq.answer.trim()) {
      setFormData({
        ...formData,
        faq: [...formData.faq, { ...newFaq }],
      });
      setNewFaq({ question: "", answer: "" });
    }
  };

  const handleRemoveFaq = (index) => {
    setFormData({
      ...formData,
      faq: formData.faq.filter((_, i) => i !== index),
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.warning("Nome do produto obrigatorio");
      return;
    }

    try {
      const data = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
        toast.success("Produto atualizado com sucesso!");
      } else {
        await createProduct(data);
        toast.success("Produto criado com sucesso!");
      }
      handleCloseForm();
    } catch (err) {
      // Error handled by store
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm("Tem certeza que deseja excluir este produto?")) {
      try {
        await deleteProduct(productId);
        toast.success("Produto excluido com sucesso!");
      } catch (err) {
        // Error handled by store
      }
    }
  };

  const formatPrice = (price, priceType) => {
    if (priceType === "sob_consulta") return "Sob consulta";
    if (!price) return "Preco nao definido";
    const formatted = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
    if (priceType === "a_partir_de") return `A partir de ${formatted}`;
    return formatted;
  };

  return (
    <div className="catalog-section">
      <div className="catalog-header">
        <div>
          <h3>
            <FaBox /> Catalogo de Produtos
          </h3>
          <p>Gerencie os produtos que o agente pode apresentar</p>
        </div>
        <button className="add-button" onClick={() => handleOpenForm()}>
          <FaPlus /> Novo Produto
        </button>
      </div>

      {products.length === 0 ? (
        <div className="empty-catalog">
          <FaBox size={48} />
          <p>Nenhum produto cadastrado</p>
          <span>Adicione produtos para o agente poder apresenta-los aos clientes</span>
        </div>
      ) : (
        <div className="catalog-grid">
          {products.map((product) => (
            <div key={product.id} className="catalog-card">
              <div className="card-header">
                <h4>{product.name}</h4>
                <div className="card-actions">
                  <button onClick={() => handleOpenForm(product)} title="Editar">
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDelete(product.id)} title="Excluir">
                    <FaTrash />
                  </button>
                </div>
              </div>

              {product.short_description && (
                <p className="card-description">{product.short_description}</p>
              )}

              <div className="card-meta">
                <span className="price">
                  <FaDollarSign />
                  {formatPrice(product.price, product.price_type)}
                </span>
                {product.category && (
                  <span className="category">
                    <FaTag />
                    {product.category}
                  </span>
                )}
              </div>

              <div className="card-footer">
                <span className={`availability ${product.stock_available ? "available" : "unavailable"}`}>
                  {product.stock_available ? "Disponivel" : "Indisponivel"}
                </span>
                {product.delivery_time && (
                  <span className="delivery">Entrega: {product.delivery_time}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>{editingProduct ? "Editar Produto" : "Novo Produto"}</h3>
              <button className="close-button" onClick={handleCloseForm}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field full-width">
                  <label>Nome do Produto *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Plano Premium"
                  />
                </div>

                <div className="form-field full-width">
                  <label>Descricao Curta</label>
                  <input
                    type="text"
                    value={formData.short_description}
                    onChange={(e) =>
                      setFormData({ ...formData, short_description: e.target.value })
                    }
                    placeholder="Resumo em uma linha"
                    maxLength={500}
                  />
                </div>

                <div className="form-field full-width">
                  <label>Descricao Completa</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Descreva o produto em detalhes..."
                    rows={4}
                  />
                </div>

                <div className="form-field">
                  <label>Preco</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-field">
                  <label>Tipo de Preco</label>
                  <select
                    value={formData.price_type}
                    onChange={(e) =>
                      setFormData({ ...formData, price_type: e.target.value })
                    }
                  >
                    <option value="fixo">Preco Fixo</option>
                    <option value="a_partir_de">A partir de</option>
                    <option value="sob_consulta">Sob Consulta</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>Categoria</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    placeholder="Ex: Software, Servicos"
                  />
                </div>

                <div className="form-field">
                  <label>Prazo de Entrega</label>
                  <input
                    type="text"
                    value={formData.delivery_time}
                    onChange={(e) =>
                      setFormData({ ...formData, delivery_time: e.target.value })
                    }
                    placeholder="Ex: 2-3 dias uteis"
                  />
                </div>

                <div className="form-field checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.stock_available}
                      onChange={(e) =>
                        setFormData({ ...formData, stock_available: e.target.checked })
                      }
                    />
                    Disponivel em estoque
                  </label>
                </div>

                <div className="form-field full-width">
                  <label>Palavras-chave</label>
                  <p className="field-hint">Ajudam o agente a encontrar este produto</p>
                  <div className="tag-input-container">
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      placeholder="Digite uma palavra-chave"
                      onKeyPress={(e) => e.key === "Enter" && handleAddKeyword()}
                    />
                    <button type="button" onClick={handleAddKeyword}>
                      Adicionar
                    </button>
                  </div>
                  <div className="tags-list">
                    {formData.keywords.map((keyword) => (
                      <span key={keyword} className="tag">
                        {keyword}
                        <button onClick={() => handleRemoveKeyword(keyword)}>&times;</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="form-field full-width">
                  <label>FAQ do Produto</label>
                  <p className="field-hint">Perguntas frequentes sobre este produto</p>
                  <div className="faq-input">
                    <input
                      type="text"
                      value={newFaq.question}
                      onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                      placeholder="Pergunta"
                    />
                    <input
                      type="text"
                      value={newFaq.answer}
                      onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                      placeholder="Resposta"
                    />
                    <button type="button" onClick={handleAddFaq}>
                      <FaPlus />
                    </button>
                  </div>
                  <div className="faq-list">
                    {formData.faq.map((item, index) => (
                      <div key={index} className="faq-item">
                        <strong>P: {item.question}</strong>
                        <p>R: {item.answer}</p>
                        <button onClick={() => handleRemoveFaq(index)}>
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="cancel-button" onClick={handleCloseForm}>
                Cancelar
              </button>
              <button className="save-button" onClick={handleSave} disabled={loading}>
                <FaSave /> {loading ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCatalog;
